import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getBuyerFeedbackSurvey,
  saveBuyerFeedbackPartial,
  submitBuyerFeedbackSurvey,
} from '../../api/buyerFeedback';
import { ANALYTICS_EVENTS, pushEvent } from '../../utils/analytics';
import './CartAbandonFeedbackPage.css';

const DEFAULT_SURVEY_DEFINITION = {
  surveyType: 'cart_abandon_feedback',
  surveyVersion: 'cart_abandon_feedback_v3',
  requiredMinChars: 25,
  maxAiFollowups: 6,
  incentives: {
    surveyUsd: 50,
    callUsd: 0,
  },
  questions: [
  {
    key: 'expected',
    required: true,
    label: 'What were you trying to do with BotSpot?',
    helper: 'Tell us what you hoped BotSpot would help you do.',
    placeholder: 'Example: I wanted to test a strategy idea without coding.',
  },
  {
    key: 'interest',
    required: true,
    label: 'What made you interested enough to check it out?',
    helper: 'What caught your attention before you reached the decision point?',
    placeholder: 'Example: I liked the idea of turning a strategy into a bot quickly.',
  },
  {
    key: 'stopped',
    required: true,
    label: 'What made you decide not to pay today?',
    helper: 'Be blunt. Price, trust, proof, broker setup, timing, confusion, or anything else.',
    placeholder: 'Example: I was not sure what would happen after I paid.',
  },
  {
    key: 'unclear_or_risky',
    required: true,
    label: 'What felt unclear, risky, or missing?',
    helper: 'Think about trust, setup, broker connection, security, proof, or next steps.',
    placeholder: 'Example: I wanted to know exactly how broker setup worked.',
  },
  {
    key: 'alternatives',
    required: true,
    label: 'What else are you considering instead?',
    helper: 'This could be another tool, doing it yourself, waiting, or doing nothing.',
    placeholder: 'Example: I was also looking at TradingView alerts or hiring a developer.',
  },
  {
    key: 'worth_paying',
    required: true,
    label: 'What would have made you comfortable paying today?',
    helper: 'What proof, change, explanation, or offer would have made the difference?',
    placeholder: 'Example: a quick demo, clearer setup steps, or stronger proof.',
  },
  ],
};

const normalizeSurveyDefinition = (definition) => {
  const source = definition && typeof definition === 'object' ? definition : DEFAULT_SURVEY_DEFINITION;
  const questions = Array.isArray(source.questions) && source.questions.length > 0
    ? source.questions
    : DEFAULT_SURVEY_DEFINITION.questions;
  return {
    ...DEFAULT_SURVEY_DEFINITION,
    ...source,
    incentives: {
      ...DEFAULT_SURVEY_DEFINITION.incentives,
      ...(source.incentives || {}),
    },
    questions: questions.filter((question) => question?.key && question?.label),
  };
};

const answerLength = (value) => (typeof value === 'string' ? value.trim().length : 0);
const isEnough = (value, minChars) => answerLength(value) >= minChars;
const charactersLeft = (value, minChars) => Math.max(0, minChars - answerLength(value));
const detailStatus = (value, minChars) => {
  const remaining = charactersLeft(value, minChars);
  if (remaining <= 0) return 'Enough detail';
  return `${remaining} ${remaining === 1 ? 'character' : 'characters'} left`;
};

const followupsFor = (followups, questionKey) => (
  followups.filter((followup) => followup?.questionKey === questionKey).slice(0, 1)
);

const requiredKeysFor = (questions) => questions
  .filter((question) => question.required !== false)
  .map((question) => question.key);

const answeredRequired = (answers, requiredKeys, minChars) => (
  requiredKeys.filter((key) => isEnough(answers[key], minChars)).length
);

const analyticsPayload = (extra = {}) => ({
  source: 'cart_abandon_feedback',
  page_path: '/feedback/why-not-join',
  survey_version: DEFAULT_SURVEY_DEFINITION.surveyVersion,
  ...extra,
});

function StatusMessage({ type = 'info', children }) {
  return <div className={`feedback-status feedback-status-${type}`}>{children}</div>;
}

function currentQuestionFromAnswers(answers, questions, minChars) {
  const firstMissing = questions.findIndex((question) => !isEnough(answers[question.key], minChars));
  return firstMissing === -1 ? questions.length - 1 : firstMissing;
}

function CartAbandonFeedbackPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFollowupKey, setPendingFollowupKey] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const hasLoadedRef = useRef(false);

  const surveyDefinition = useMemo(() => normalizeSurveyDefinition(survey?.surveyDefinition), [survey?.surveyDefinition]);
  const questions = surveyDefinition.questions;
  const requiredKeys = useMemo(() => requiredKeysFor(questions), [questions]);
  const minRequiredChars = surveyDefinition.requiredMinChars || DEFAULT_SURVEY_DEFINITION.requiredMinChars;
  const surveyVersion = surveyDefinition.surveyVersion || DEFAULT_SURVEY_DEFINITION.surveyVersion;
  const surveyUsd = survey?.surveyIncentiveUsd ?? surveyDefinition.incentives?.surveyUsd ?? 50;
  const callUsd = survey?.callIncentiveUsd ?? surveyDefinition.incentives?.callUsd ?? 0;
  const hasCallOffer = callUsd > 0 && Boolean(survey?.calendlyUrl);
  const currentQuestion = questions[stepIndex] || questions[0];
  const currentFollowups = followupsFor(followups, currentQuestion.key);
  const currentFollowup = currentFollowups[0] || null;
  const currentFollowupKey = currentFollowup ? `ai_followup_${currentFollowup.id}` : null;
  const progressCount = answeredRequired(answers, requiredKeys, minRequiredChars);
  const currentBaseAnswered = isEnough(answers[currentQuestion.key], minRequiredChars);
  const currentFollowupAnswered = !currentFollowupKey || isEnough(answers[currentFollowupKey], minRequiredChars);
  const canGoNext = currentBaseAnswered && currentFollowupAnswered && !saving && !submitting;
  const isLastStep = stepIndex >= questions.length - 1;
  const needsLastStepFollowup = isLastStep && !currentFollowupKey;
  const canSubmit = (
    isLastStep
    && Boolean(currentFollowupKey)
    && progressCount === requiredKeys.length
    && currentFollowupAnswered
    && !saving
    && !submitting
    && !submitted
  );

  useEffect(() => {
    let mounted = true;

    const loadSurvey = async () => {
      if (!token) {
        setError({ code: 'token_missing', message: 'This survey link is missing its invite token.' });
        setLoading(false);
        pushEvent({
          event: ANALYTICS_EVENTS.BUYER_FEEDBACK_TOKEN_INVALID,
          ...analyticsPayload({ reason: 'missing_token' }),
        });
        return;
      }

      try {
        setLoading(true);
        const payload = await getBuyerFeedbackSurvey(token);
        if (!mounted) return;
        const nextSurvey = payload.survey;
        const nextAnswers = nextSurvey.partialAnswers || {};
        setSurvey(nextSurvey);
        setAnswers(nextAnswers);
        setFollowups(Array.isArray(nextSurvey.aiFollowups) ? nextSurvey.aiFollowups : []);
        setSubmitted(Boolean(nextSurvey.submittedAt));
        setStarted(Object.values(nextAnswers).some((value) => answerLength(value) > 0));
        const nextDefinition = normalizeSurveyDefinition(nextSurvey.surveyDefinition);
        setStepIndex(currentQuestionFromAnswers(nextAnswers, nextDefinition.questions, nextDefinition.requiredMinChars));
        hasLoadedRef.current = true;
        pushEvent({
          event: ANALYTICS_EVENTS.BUYER_FEEDBACK_SURVEY_VIEWED,
          ...analyticsPayload({
            token_id: nextSurvey.id,
            country_code: nextSurvey.countryCode,
            already_submitted: Boolean(nextSurvey.submittedAt),
          }),
        });
      } catch (err) {
        if (!mounted) return;
        setError({ code: err.code || 'load_failed', message: err.message });
        pushEvent({
          event: err.code === 'token_expired'
            ? ANALYTICS_EVENTS.BUYER_FEEDBACK_TOKEN_EXPIRED
            : ANALYTICS_EVENTS.BUYER_FEEDBACK_TOKEN_INVALID,
          ...analyticsPayload({ reason: err.code || 'load_failed' }),
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSurvey();
    return () => {
      mounted = false;
    };
  }, [token]);

  const mergeFollowup = useCallback((followUp) => {
    if (!followUp?.id || !followUp?.prompt) return;
    setFollowups((current) => {
      if (current.some((item) => item.id === followUp.id)) return current;
      if (current.some((item) => item.questionKey === followUp.questionKey)) return current;
      pushEvent({
        event: ANALYTICS_EVENTS.BUYER_FEEDBACK_AI_FOLLOWUP_SHOWN,
        ...analyticsPayload({
          token_id: survey?.id,
          question_key: followUp.questionKey,
        }),
      });
      return [...current, followUp].slice(0, surveyDefinition.maxAiFollowups || questions.length);
    });
  }, [questions.length, survey?.id, surveyDefinition.maxAiFollowups]);

  const savePartialNow = useCallback(async (questionKey, nextAnswers = answers, options = {}) => {
    if (!token || submitted || !hasLoadedRef.current) return null;
    if (Object.values(nextAnswers).every((value) => answerLength(value) === 0)) return null;
    try {
      setSaving(true);
      setPendingFollowupKey(options.generateFollowup ? questionKey : null);
      const payload = await saveBuyerFeedbackPartial({
        token,
        answers: nextAnswers,
        questionKey,
        surveyVersion,
        stepIndex,
        notifyImmediately: Boolean(options.notifyImmediately),
        generateFollowup: Boolean(options.generateFollowup),
      });
      setSurvey(payload.survey);
      if (Array.isArray(payload.survey?.aiFollowups)) {
        setFollowups(payload.survey.aiFollowups);
      }
      mergeFollowup(payload.followUp);
      pushEvent({
        event: ANALYTICS_EVENTS.BUYER_FEEDBACK_PARTIAL_SAVED,
        ...analyticsPayload({
          token_id: payload.survey?.id || survey?.id,
          question_key: questionKey,
          answer_count: Object.values(nextAnswers).filter((value) => answerLength(value) > 0).length,
          notify_immediately: Boolean(options.notifyImmediately),
          generate_followup: Boolean(options.generateFollowup),
        }),
      });
      return payload;
    } catch (err) {
      setError({ code: err.code || 'save_failed', message: err.message });
      return null;
    } finally {
      setSaving(false);
      setPendingFollowupKey(null);
    }
  }, [answers, mergeFollowup, stepIndex, submitted, survey?.id, surveyVersion, token]);

  const updateAnswer = (key, value) => {
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);
    pushEvent({
      event: ANALYTICS_EVENTS.BUYER_FEEDBACK_QUESTION_ANSWERED,
      ...analyticsPayload({
        token_id: survey?.id,
        question_key: key,
        answer_length: answerLength(value),
      }),
    });
  };

  const startSurvey = () => {
    setStarted(true);
    pushEvent({
      event: ANALYTICS_EVENTS.BUYER_FEEDBACK_SURVEY_VIEWED,
      ...analyticsPayload({ token_id: survey?.id, action: 'started' }),
    });
  };

  const goBack = () => {
    setError(null);
    if (stepIndex === 0) {
      setStarted(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setStepIndex((current) => Math.max(0, current - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goNext = async () => {
    setError(null);
    if (!canGoNext) {
      setError({
        code: 'answer_too_short',
        message: currentFollowup
          ? 'Answer the follow-up before moving on.'
          : 'Add one or two more details before moving on.',
      });
      return;
    }
    if (currentFollowupKey) {
      const followupSave = await savePartialNow(currentFollowupKey, answers, {
        notifyImmediately: false,
        generateFollowup: false,
      });
      if (!followupSave) return;
      if (!isLastStep) {
        setStepIndex((current) => Math.min(questions.length - 1, current + 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    const payload = await savePartialNow(currentQuestion.key, answers, {
      notifyImmediately: false,
      generateFollowup: true,
    });
    if (!payload) return;
    if (payload?.followUp?.questionKey === currentQuestion.key) {
      return;
    }
    if (!isLastStep) {
      setStepIndex((current) => Math.min(questions.length - 1, current + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submitSurvey = async (event) => {
    event.preventDefault();
    setError(null);
    if (!canSubmit) {
      setError({
        code: 'missing_required',
        message: currentFollowup
          ? 'Answer the follow-up before submitting.'
          : `Answer all ${requiredKeys.length} questions before submitting.`,
      });
      return;
    }
    try {
      setSubmitting(true);
      if (currentFollowupKey) {
        const followupSave = await savePartialNow(currentFollowupKey, answers, {
          notifyImmediately: false,
          generateFollowup: false,
        });
        if (!followupSave) return;
      } else {
        const partialPayload = await savePartialNow(currentQuestion.key, answers, {
          notifyImmediately: false,
          generateFollowup: true,
        });
        if (!partialPayload) return;
        if (partialPayload?.followUp?.questionKey === currentQuestion.key) {
          return;
        }
      }
      const payload = await submitBuyerFeedbackSurvey({
        token,
        answers,
        questionKey: currentQuestion.key,
        surveyVersion,
        stepIndex,
        callInterest: false,
        callPreferredWindows: null,
      });
      setSurvey(payload.survey);
      setSubmitted(true);
      pushEvent({
        event: ANALYTICS_EVENTS.BUYER_FEEDBACK_SURVEY_SUBMITTED,
        ...analyticsPayload({
          token_id: payload.survey?.id || survey?.id,
          call_interest: false,
          answer_count: Object.values(answers).filter((value) => answerLength(value) > 0).length,
        }),
      });
    } catch (err) {
      setError({ code: err.code || 'submit_failed', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const trackCalendlyClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.BUYER_FEEDBACK_CALENDLY_CLICKED,
      ...analyticsPayload({ token_id: survey?.id }),
    });
  };

  const followupLabel = useMemo(() => {
    if (!currentFollowup) return null;
    return currentFollowup.prompt;
  }, [currentFollowup]);

  if (loading) {
    return (
      <div className="cart-abandon-feedback-page">
        <StatusMessage>Loading your feedback link...</StatusMessage>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="cart-abandon-feedback-page">
        <section className="feedback-shell feedback-shell-centered">
          <p className="feedback-kicker">${surveyUsd} feedback survey</p>
          <h1>This feedback link is not working.</h1>
          <StatusMessage type="error">{error.message}</StatusMessage>
          <p className="feedback-muted">Reply to the message Rob sent you and we can fix the invite.</p>
        </section>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="cart-abandon-feedback-page">
        <section className="feedback-shell feedback-shell-centered">
          <p className="feedback-kicker">${surveyUsd} feedback survey</p>
          <h1>Thanks. We have your answers.</h1>
          <p className="feedback-lede">
            We will review the answers and send the ${surveyUsd} gift card if the response is real and useful.
          </p>
          {hasCallOffer && (
            <a
              className="feedback-primary-link"
              href={survey.calendlyUrl}
              target="_blank"
              rel="noreferrer"
              onClick={trackCalendlyClick}
            >
              Book the ${callUsd} feedback call
            </a>
          )}
        </section>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="cart-abandon-feedback-page">
        <section className="feedback-shell feedback-shell-intro">
          <h1>Help us understand why you did not join BotSpot</h1>
          <p className="feedback-lede">
            Answer {requiredKeys.length} quick questions about what stopped you, and we will send
            you a ${surveyUsd} gift card after reviewing the answers.
          </p>
          <div className="feedback-offer-pill" aria-label={`$${surveyUsd} survey gift card`}>
            <span><strong>${surveyUsd}</strong> gift card</span>
            <span>{requiredKeys.length} questions</span>
            <span>6-8 minutes</span>
          </div>
          <button className="feedback-primary-link" type="button" onClick={startSurvey}>
            Start the ${surveyUsd} survey
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="cart-abandon-feedback-page">
      <section className="feedback-shell feedback-shell-question">
        <form className="feedback-step-form" onSubmit={submitSurvey}>
          <div className="feedback-step-topline">
            <span>Question {stepIndex + 1} of {questions.length}</span>
            <span>${surveyUsd} survey</span>
          </div>
          <div className="feedback-progress-track" aria-hidden="true">
            <div className="feedback-progress-fill" style={{ width: `${((stepIndex + 1) / questions.length) * 100}%` }} />
          </div>

          <div className="feedback-step-card">
            <label className="feedback-step-label" htmlFor={currentQuestion.key}>
              {currentQuestion.label}
            </label>
            <p className="feedback-hint">{currentQuestion.helper}</p>
            <textarea
              id={currentQuestion.key}
              value={answers[currentQuestion.key] || ''}
              onChange={(event) => updateAnswer(currentQuestion.key, event.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={5}
              minLength={minRequiredChars}
              autoFocus
            />
            <div className="feedback-detail-row">
              <span>{detailStatus(answers[currentQuestion.key], minRequiredChars)}</span>
            </div>
          </div>

          {pendingFollowupKey === currentQuestion.key && (
            <p className="feedback-ai-pending">Writing one quick follow-up...</p>
          )}

          {currentFollowup && (
            <div className="feedback-step-card feedback-followup">
              <p className="feedback-followup-kicker">One quick follow-up</p>
              <label className="feedback-step-label feedback-followup-label" htmlFor={currentFollowupKey}>
                {followupLabel}
              </label>
              <textarea
                id={currentFollowupKey}
                value={answers[currentFollowupKey] || ''}
                onChange={(event) => {
                  updateAnswer(currentFollowupKey, event.target.value);
                  pushEvent({
                    event: ANALYTICS_EVENTS.BUYER_FEEDBACK_AI_FOLLOWUP_ANSWERED,
                    ...analyticsPayload({
                      token_id: survey?.id,
                      question_key: currentQuestion.key,
                      answer_length: answerLength(event.target.value),
                    }),
                  });
                }}
                placeholder="A short answer is fine."
                rows={3}
                minLength={minRequiredChars}
              />
              <div className="feedback-detail-row">
                <span>{detailStatus(answers[currentFollowupKey], minRequiredChars)}</span>
              </div>
            </div>
          )}

          {error && <StatusMessage type="error">{error.message}</StatusMessage>}

          <div className="feedback-step-actions">
            <button
              type="button"
              className="feedback-secondary-button"
              onClick={goBack}
              disabled={submitting}
            >
              Back
            </button>
            {(!isLastStep || needsLastStepFollowup) ? (
              <button
                type="button"
                className="feedback-submit"
                onClick={goNext}
                disabled={!canGoNext}
              >
                Next
              </button>
            ) : (
              <button className="feedback-submit" type="submit" disabled={!canSubmit}>
                {submitting ? 'Submitting...' : 'Submit the survey'}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

export default CartAbandonFeedbackPage;
